import { create } from 'zustand';
import { LocalStore } from '@/platform/localStore';
import type { ChildProfile } from '@/contracts/family';
import { SyncService } from './sync';

/**
 * Session + family context.
 *
 * Today this runs entirely on-device (`account = 'guest'` for the public
 * sample, or a local account id once a parent sets up). When a backend is
 * provisioned, `account` becomes the server-issued owner id and every read
 * here goes through the same service functions — no component changes.
 *
 * The route's `:childId` is a navigation reference only. `requireChild()`
 * resolves it against the CURRENT account, so a guessed id in the URL
 * resolves to nothing (spec §3).
 */

export type AdultGrant = { grantedAt: number; expiresAt: number } | null;

/** Adult elevation is short-lived; loading a parent screen is not enough (§6.3). */
const GRANT_MS = 10 * 60 * 1000;

interface SessionState {
  accountId: string;
  children: ChildProfile[];
  activeChildId: string | null;
  adultGrant: AdultGrant;
  loaded: boolean;

  load(): Promise<void>;
  addChild(input: Omit<ChildProfile, 'id' | 'ownerUserId' | 'createdAt' | 'reviewedNickname'>): Promise<ChildProfile>;
  updateChild(id: string, patch: Partial<ChildProfile>): Promise<void>;
  removeChild(id: string): Promise<void>;
  setActiveChild(id: string | null): void;
  requireChild(id: string | undefined): ChildProfile | null;
  grantAdult(): void;
  revokeAdult(): void;
  hasAdultGrant(): boolean;
}

const ACCOUNT_KEY = 'examine.accountId';

function currentAccount(): string {
  try {
    const existing = localStorage.getItem(ACCOUNT_KEY);
    if (existing) return existing;
    const fresh = `local_${crypto.randomUUID()}`;
    localStorage.setItem(ACCOUNT_KEY, fresh);
    return fresh;
  } catch {
    // Private mode with storage blocked — stay in memory for this session.
    return 'guest';
  }
}

export const useSession = create<SessionState>((set, get) => ({
  accountId: currentAccount(),
  children: [],
  activeChildId: null,
  adultGrant: null,
  loaded: false,

  async load() {
    const account = get().accountId;
    const children = await LocalStore.list<ChildProfile>('children', account);
    children.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const lastActive = await LocalStore.get<string>('meta', account, 'activeChildId');
    set({
      children,
      activeChildId: children.some((c) => c.id === lastActive) ? lastActive! : null,
      loaded: true,
    });

    // Sync starts AFTER local data is on screen, and never blocks it. With no
    // service configured this returns immediately and changes nothing.
    void SyncService.restore(account).then(() => SyncService.run()).then(async () => {
      // A pull may have brought children from another device.
      const merged = await LocalStore.list<ChildProfile>('children', account);
      if (merged.length !== children.length) {
        merged.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        set({ children: merged });
      }
    });
  },

  async addChild(input) {
    const account = get().accountId;
    const child: ChildProfile = {
      ...input,
      id: `child_${crypto.randomUUID()}`,
      ownerUserId: account,
      createdAt: new Date().toISOString(),
      reviewedNickname: null,
    };
    await LocalStore.put('children', account, child.id, child);
    void SyncService.queue('children', child.id, { childId: child.id });
    set({ children: [...get().children, child] });
    return child;
  },

  async updateChild(id, patch) {
    const account = get().accountId;
    const child = get().children.find((c) => c.id === id);
    if (!child) return;
    // ownerUserId and id can never be reassigned from the client.
    const next: ChildProfile = { ...child, ...patch, id: child.id, ownerUserId: child.ownerUserId };

    // Apply to state first so a toggle responds immediately instead of
    // visibly bouncing back while the durable write completes.
    set({ children: get().children.map((c) => (c.id === id ? next : c)) });
    try {
      await LocalStore.put('children', account, id, next);
      void SyncService.queue('children', id, { childId: id });
    } catch (err) {
      // The write failed, so the setting did not stick — put the control back
      // rather than leave the UI showing a change that was not saved.
      set({ children: get().children.map((c) => (c.id === id ? child : c)) });
      throw err;
    }
  },

  async removeChild(id) {
    const account = get().accountId;
    await LocalStore.delete('children', account, id);
    set({
      children: get().children.filter((c) => c.id !== id),
      activeChildId: get().activeChildId === id ? null : get().activeChildId,
    });
  },

  setActiveChild(id) {
    set({ activeChildId: id });
    void LocalStore.put('meta', get().accountId, 'activeChildId', id);
  },

  requireChild(id) {
    if (!id) return null;
    return get().children.find((c) => c.id === id && c.ownerUserId === get().accountId) ?? null;
  },

  grantAdult() {
    const now = Date.now();
    set({ adultGrant: { grantedAt: now, expiresAt: now + GRANT_MS } });
  },

  revokeAdult() {
    set({ adultGrant: null });
  },

  hasAdultGrant() {
    const g = get().adultGrant;
    return !!g && g.expiresAt > Date.now();
  },
}));
